/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Project detail — /real-estate/projects/:id
 *
 * GET /re/projects/{id} (embeds developer, buildings, unit_types, amenities)
 * GET /re/projects/{id}/summary, PUT /re/projects/{id}
 * GET/POST /re/projects/{id}/construction-updates, POST /re/projects/{id}/handover
 * PUT /re/projects/{id}/amenities, GET /re/amenities
 * GET /re/units, /re/price-lists (+ POST apply), /re/payment-plans filtered by project_id
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Icon, Link, Skeleton,
  Tab, Tabs, TextField,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import InfoCard from './components/ReInfoCard';
import DetailField from 'components/detail/DetailField';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { usePermission } from 'auth/usePermission';
import RePage, { LoadError, RePanel, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import { DateField, KpiTile, Num, ProgressBar } from './components/Widgets';
import {
  PROJECT_STATUSES, UNIT_STATUSES, bedroomsLabel, errorMessage, formatDate, formatMoney, formatNumber, formatPct,
  humanize, todayIso, toNumber, toOptions, useReApi, useReGet, useReList,
} from './api';
import { choiceOptions, defaultCurrency, isHidden } from '../variant';
import useReVariant from '../useReVariant';

const EDIT_FIELDS = [
  { field: 'name', label: 'Name' },
  { field: 'code', label: 'Code' },
  { field: 'status', label: 'Status', options: toOptions(PROJECT_STATUSES) },
  { field: 'master_developer', label: 'Master developer' },
  { field: 'community', label: 'Community' },
  { field: 'sub_community', label: 'Sub-community' },
  { field: 'city', label: 'City' },
  { field: 'emirate', label: 'Emirate' },
  { field: 'rera_project_no', label: 'RERA project no.' },
  { field: 'dld_project_no', label: 'DLD project no.' },
  { field: 'escrow_bank', label: 'Escrow bank' },
  { field: 'escrow_account_no', label: 'Escrow account no.' },
  { field: 'launch_date', label: 'Launch date', type: 'date' },
  { field: 'expected_handover_date', label: 'Expected handover', type: 'date' },
  { field: 'dld_fee_pct', label: 'DLD fee %', type: 'number' },
  { field: 'admin_fee_amount', label: 'Admin fee', type: 'number', money: true },
  { field: 'oqood_fee_amount', label: 'Oqood fee', type: 'number', money: true },
  // Australia (hidden for UAE tenants)
  { field: 'state', label: 'State', region: true },
  { field: 'postcode', label: 'Postcode' },
  { field: 'council', label: 'Council' },
  { field: 'strata_plan_no', label: 'Strata plan no.' },
  { field: 'brochure_url', label: 'Brochure URL' },
  { field: 'cover_image_url', label: 'Cover image URL' },
  { field: 'description', label: 'Description', multiline: true },
];

const NUMERIC_FIELDS = new Set(['dld_fee_pct', 'admin_fee_amount', 'oqood_fee_amount']);

/** The edit fields in the tenant's country: hidden columns dropped, the
 * region list and label and the currency filled in. */
const editFieldsFor = (pack) => EDIT_FIELDS
  .filter((f) => !isHidden('re_projects', f.field, pack))
  .map((f) => {
    if (f.money) return { ...f, label: `${f.label} (${defaultCurrency(pack)})` };
    if (f.region) return { ...f, label: pack.region_label, options: choiceOptions(pack.regions) };
    return f;
  });

function Overview({ projectId, project }) {
  const { data: s, loading, error, reload } = useReGet(`/re/projects/${projectId}/summary`);
  const cur = project.currency || defaultCurrency();
  return (
    <>
      <LoadError error={error} onRetry={reload} />
      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} md={3}><KpiTile loading={loading} label="Total units" icon="apartment" value={formatNumber(s?.total_units)} /></Grid>
        <Grid item xs={6} md={3}><KpiTile loading={loading} label="Inventory value" icon="inventory_2" value={formatMoney(s?.inventory_value, cur, { compact: true })} /></Grid>
        <Grid item xs={6} md={3}><KpiTile loading={loading} label="Sold value" icon="sell" tone="success" value={formatMoney(s?.sold_value, cur, { compact: true })} hint={`${formatNumber(s?.bookings)} bookings`} /></Grid>
        <Grid item xs={6} md={3}><KpiTile loading={loading} label="Collected" icon="account_balance" tone="success" value={formatMoney(s?.collected, cur, { compact: true })} hint={`${formatMoney(s?.outstanding, cur, { compact: true })} outstanding`} /></Grid>
      </Grid>
      <InfoCard icon="donut_small" title="Units by status">
        <MDBox display="flex" flexWrap="wrap" gap={1.5}>
          {UNIT_STATUSES.map((st) => (
            <MDBox key={st} display="flex" alignItems="center" gap={1}>
              <ReStatusChip vocab="unit" status={st} />
              <Num bold>{formatNumber(s?.unit_counts?.[st] || 0)}</Num>
            </MDBox>
          ))}
        </MDBox>
      </InfoCard>
    </>
  );
}

function SimpleList({ path, params, columns, empty, onRowClick }) {
  const list = useReList(path, params);
  return (
    <>
      <LoadError error={list.error} onRetry={list.reload} />
      <ReTable
        columns={columns}
        rows={list.items}
        loading={list.loading}
        emptyMessage={empty}
        onRowClick={onRowClick}
        total={list.total}
        page={list.page}
        pageSize={list.pageSize}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
      />
    </>
  );
}

function PriceLists({ projectId, notify, canEdit }) {
  const { api } = useReApi();
  const navigate = useNavigate();
  const list = useReList('/re/price-lists', { project_id: projectId });
  const apply = async (pl) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Copy the prices in "${pl.name}" onto the units' list prices?`)) return;
    try {
      const res = await api.post(`/re/price-lists/${pl.id}/apply`);
      notify(`${formatNumber(res.data?.updated)} unit prices updated`);
    } catch (e) {
      notify.error(e, 'Could not apply the price list');
    }
  };
  return (
    <ReTable
      loading={list.loading}
      rows={list.items}
      emptyMessage="No price lists."
      onRowClick={(r) => navigate(`/real-estate/price-lists/${r.id}`)}
      total={list.total}
      page={list.page}
      pageSize={list.pageSize}
      onPageChange={list.setPage}
      onPageSizeChange={list.setPageSize}
      columns={[
        { key: 'name', label: 'Name' },
        { key: 'effective_from', label: 'From', render: (r) => formatDate(r.effective_from) },
        { key: 'effective_to', label: 'To', render: (r) => formatDate(r.effective_to) },
        { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
        {
          key: 'actions',
          label: '',
          align: 'right',
          render: (r) => (canEdit ? (
            <MDButton size="small" variant="text" color="info" onClick={(e) => { e.stopPropagation(); apply(r); }}>
              Apply to units
            </MDButton>
          ) : null),
        },
      ]}
    />
  );
}

function ConstructionUpdates({ projectId, notify, canEdit, onProjectChanged }) {
  const { api } = useReApi();
  const updates = useReGet(`/re/projects/${projectId}/construction-updates`);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ progress_pct: '', as_of_date: todayIso(), certified_by: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const rows = Array.isArray(updates.data) ? updates.data : updates.data?.items || [];

  const submit = async () => {
    const pct = toNumber(form.progress_pct);
    if (pct === null || pct < 0 || pct > 100) {
      setErr('Progress must be between 0 and 100.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const res = await api.post(`/re/projects/${projectId}/construction-updates`, {
        progress_pct: pct, as_of_date: form.as_of_date, certified_by: form.certified_by || null, notes: form.notes || null,
      });
      const n = res.data?.installments_dated || 0;
      notify(`Progress recorded at ${pct}%. ${n} construction-linked installment${n === 1 ? '' : 's'} now dated.`);
      setOpen(false);
      updates.reload();
      onProjectChanged();
    } catch (e) {
      setErr(errorMessage(e, 'Could not record progress'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <MDBox display="flex" justifyContent="flex-end" mb={1}>
        {canEdit && (
          <MDButton variant="gradient" color="info" size="small" onClick={() => { setErr(null); setOpen(true); }}>
            <Icon>add</Icon>&nbsp;Record progress
          </MDButton>
        )}
      </MDBox>
      <LoadError error={updates.error} onRetry={updates.reload} />
      <ReTable
        loading={updates.loading}
        rows={rows}
        emptyMessage="No construction updates recorded."
        columns={[
          { key: 'as_of_date', label: 'As of', render: (r) => formatDate(r.as_of_date) },
          { key: 'progress_pct', label: 'Progress', render: (r) => <Num bold>{formatPct(r.progress_pct)}</Num> },
          { key: 'certified_by', label: 'Certified by' },
          { key: 'notes', label: 'Notes' },
          { key: 'created_by_name', label: 'Recorded by' },
        ]}
      />
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Record construction progress</DialogTitle>
        <DialogContent>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          <Alert severity="info" sx={{ mb: 2 }}>
            Installments linked to construction % are dated automatically once this threshold is reached.
          </Alert>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField size="small" type="number" label="Progress %" value={form.progress_pct} onChange={(e) => setForm({ ...form, progress_pct: e.target.value })} />
            <DateField label="As of date" value={form.as_of_date} onChange={(v) => setForm({ ...form, as_of_date: v })} />
            <TextField size="small" label="Certified by (consultant / escrow engineer)" value={form.certified_by} onChange={(e) => setForm({ ...form, certified_by: e.target.value })} />
            <TextField size="small" multiline minRows={2} label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setOpen(false)}>Cancel</MDButton>
          <MDButton variant="gradient" color="info" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Record'}</MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

function Amenities({ projectId, project, notify, canEdit, onSaved }) {
  const { api } = useReApi();
  const all = useReGet('/re/amenities', { page_size: 200 });
  const allItems = useMemo(() => (Array.isArray(all.data) ? all.data : all.data?.items || []), [all.data]);
  const initial = useMemo(() => (project.amenities || []).map((a) => a.id ?? a.amenity_id), [project.amenities]);
  const [chosen, setChosen] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setChosen(initial); }, [initial]);

  const grouped = useMemo(() => {
    const g = {};
    allItems.forEach((a) => {
      const k = a.category || 'General';
      (g[k] = g[k] || []).push(a);
    });
    return g;
  }, [allItems]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/re/projects/${projectId}/amenities`, { amenity_ids: chosen });
      notify('Amenities saved');
      onSaved();
    } catch (e) {
      notify.error(e, 'Could not save amenities');
    } finally {
      setSaving(false);
    }
  };

  if (all.loading) return <Skeleton variant="rectangular" height={120} />;
  return (
    <>
      <LoadError error={all.error} onRetry={all.reload} />
      {!allItems.length && <MDTypography variant="button" color="text">No amenities defined yet.</MDTypography>}
      {Object.entries(grouped).map(([cat, items]) => (
        <MDBox key={cat} mb={2}>
          <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ textTransform: 'uppercase' }}>{cat}</MDTypography>
          <MDBox display="flex" flexWrap="wrap">
            {items.map((a) => (
              <FormControlLabel
                key={a.id}
                sx={{ minWidth: 220 }}
                control={(
                  <Checkbox
                    size="small"
                    disabled={!canEdit}
                    checked={chosen.includes(a.id)}
                    onChange={() => setChosen((c) => (c.includes(a.id) ? c.filter((x) => x !== a.id) : [...c, a.id]))}
                  />
                )}
                label={<MDBox display="flex" alignItems="center" gap={0.5}>{a.icon && <Icon fontSize="small">{a.icon}</Icon>}{a.name}</MDBox>}
              />
            ))}
          </MDBox>
        </MDBox>
      ))}
      {canEdit && allItems.length > 0 && (
        <MDButton variant="gradient" color="info" size="small" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save amenities'}</MDButton>
      )}
    </>
  );
}

function Media({ project }) {
  const media = Array.isArray(project.media) ? project.media : [];
  const links = [
    project.brochure_url && { label: 'Brochure', url: project.brochure_url },
    project.cover_image_url && { label: 'Cover image', url: project.cover_image_url },
    ...media.map((m, i) => (typeof m === 'string'
      ? { label: `Media ${i + 1}`, url: m }
      : { label: m.name || m.title || m.type || `Media ${i + 1}`, url: m.url })),
  ].filter((x) => x && x.url);
  if (!links.length) return <MDTypography variant="button" color="text">No documents or media linked. Add a brochure or cover image URL in the project fields.</MDTypography>;
  return (
    <MDBox display="flex" flexDirection="column" gap={1}>
      {project.cover_image_url && (
        <MDBox component="img" src={project.cover_image_url} alt="" sx={{ maxWidth: 360, borderRadius: '8px', border: '1px solid #E8EDF5' }} />
      )}
      {links.map((l) => (
        <Link key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
          <Icon fontSize="small">description</Icon>{l.label}
        </Link>
      ))}
    </MDBox>
  );
}

function HandoverDialog({ open, onClose, projectId, notify, onDone }) {
  const { api } = useReApi();
  const [date, setDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/re/projects/${projectId}/handover`, { handover_date: date });
      notify('Handover recorded. Handover-linked installments are now dated.');
      onDone();
    } catch (e) {
      notify.error(e, 'Could not record handover');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Record handover</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Sets the actual handover date and dates every &quot;on handover&quot; and &quot;months after handover&quot; installment.
        </Alert>
        <DateField label="Handover date" value={date} onChange={setDate} />
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={submit} disabled={saving || !date}>Record</MDButton>
      </DialogActions>
    </Dialog>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useReApi();
  const [snack, notify] = useSnack();
  const { canEdit } = usePermission('re_projects');
  const { data: project, loading, error, reload, setData } = useReGet(`/re/projects/${id}`);
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [changes, setChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const pack = useReVariant();

  const save = async () => {
    if (!Object.keys(changes).length) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const body = {};
    Object.entries(changes).forEach(([k, v]) => {
      body[k] = NUMERIC_FIELDS.has(k) ? toNumber(v) : (v === '' ? null : v);
    });
    try {
      const res = await api.put(`/re/projects/${id}`, body);
      setData((p) => ({ ...p, ...res.data }));
      setChanges({});
      setEditing(false);
      notify('Project saved');
    } catch (e) {
      notify.error(e, 'Could not save the project');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !project) {
    return <RePage title="Project" onBack={() => navigate('/real-estate/projects')}><Skeleton variant="rectangular" height={240} /></RePage>;
  }
  if (!project) {
    return (
      <RePage title="Project" onBack={() => navigate('/real-estate/projects')}>
        <LoadError error={error} onRetry={reload} />
      </RePage>
    );
  }

  const p = project;
  return (
    <RePage
      title={p.name}
      subtitle={[p.code, p.community, p.sub_community, p.city].filter(Boolean).join(' · ')}
      onBack={() => navigate('/real-estate/projects')}
      actions={(
        <>
          <MDButton variant="outlined" color="info" size="small" onClick={() => navigate(`/real-estate/inventory?project_id=${p.id}`)}>
            <Icon>grid_view</Icon>&nbsp;Inventory grid
          </MDButton>
          {canEdit && !p.actual_handover_date && (
            <MDButton variant="outlined" color="dark" size="small" onClick={() => setHandoverOpen(true)}>Record handover</MDButton>
          )}
          {canEdit && !editing && (
            <MDButton variant="gradient" color="info" size="small" onClick={() => setEditing(true)}><Icon>edit</Icon>&nbsp;Edit</MDButton>
          )}
          {editing && (
            <>
              <MDButton variant="text" color="secondary" size="small" onClick={() => { setEditing(false); setChanges({}); reload(); }}>Cancel</MDButton>
              <MDButton variant="gradient" color="success" size="small" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</MDButton>
            </>
          )}
        </>
      )}
    >
      <RePanel sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <DetailField label="Status" value={<ReStatusChip vocab="project" status={p.status} />} md={2} />
          <DetailField label="Developer" value={p.developer?.name || p.developer_name} md={2} />
          {isHidden('re_projects', 'rera_project_no', pack)
            ? <DetailField label={pack.region_label} value={[p.state, p.postcode].filter(Boolean).join(' ')} md={2} />
            : <DetailField label="RERA project no." value={p.rera_project_no} md={2} />}
          <DetailField label="Expected handover" value={formatDate(p.expected_handover_date)} md={2} />
          <DetailField label="Actual handover" value={p.actual_handover_date ? formatDate(p.actual_handover_date) : null} md={2} />
          <Grid item xs={12} md={2}>
            <ProgressBar label="Construction" value={p.construction_progress_pct} />
          </Grid>
        </Grid>
      </RePanel>

      {editing && (
        <InfoCard icon="edit" title="Project fields">
          <Grid container spacing={2}>
            {editFieldsFor(pack).map((f) => (
              <Grid item xs={12} md={f.multiline ? 12 : 4} key={f.field}>
                <InlineEditField
                  label={f.label}
                  value={p[f.field] ?? ''}
                  options={f.options || null}
                  type={f.type || 'text'}
                  multiline={Boolean(f.multiline)}
                  isEditing
                  onSave={(v) => {
                    setChanges((c) => ({ ...c, [f.field]: v }));
                    setData((cur) => ({ ...cur, [f.field]: v }));
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </InfoCard>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ my: 2 }}>
        <Tab value="overview" label="Overview" />
        <Tab value="buildings" label="Buildings" />
        <Tab value="unit_types" label="Unit types" />
        <Tab value="units" label="Units" />
        <Tab value="price_lists" label="Price lists" />
        <Tab value="plans" label="Payment plans" />
        <Tab value="construction" label="Construction" />
        <Tab value="amenities" label="Amenities" />
        <Tab value="media" label="Documents & media" />
      </Tabs>

      <RePanel>
        {tab === 'overview' && <Overview projectId={id} project={p} />}
        {tab === 'buildings' && (
          <SimpleList
            path="/re/buildings"
            params={{ project_id: id }}
            empty="No buildings."
            onRowClick={(r) => navigate(`/real-estate/buildings/${r.id}`)}
            columns={[
              { key: 'name', label: 'Building' },
              { key: 'code', label: 'Code' },
              { key: 'floors', label: 'Floors', align: 'right' },
              { key: 'status', label: 'Status', render: (r) => humanize(r.status) || '—' },
              { key: 'expected_handover_date', label: 'Handover', render: (r) => formatDate(r.expected_handover_date) },
              { key: 'construction_progress_pct', label: 'Progress', align: 'right', render: (r) => formatPct(r.construction_progress_pct) },
            ]}
          />
        )}
        {tab === 'unit_types' && (
          <SimpleList
            path="/re/unit-types"
            params={{ project_id: id }}
            empty="No unit types."
            onRowClick={(r) => navigate(`/real-estate/unit-types/${r.id}`)}
            columns={[
              { key: 'name', label: 'Type' },
              { key: 'category', label: 'Category', render: (r) => humanize(r.category) },
              { key: 'bedrooms', label: 'Beds', render: (r) => bedroomsLabel(r.bedrooms) },
              { key: 'bua_sqft', label: 'BUA sq ft', align: 'right', render: (r) => formatNumber(r.bua_sqft) },
              { key: 'starting_price', label: 'From', align: 'right', render: (r) => <Num>{formatMoney(r.starting_price, p.currency, { decimals: 0 })}</Num> },
            ]}
          />
        )}
        {tab === 'units' && (
          <>
            <MDBox display="flex" justifyContent="flex-end" mb={1}>
              <MDButton size="small" variant="text" color="info" onClick={() => navigate(`/real-estate/inventory?project_id=${p.id}`)}>
                Open availability grid&nbsp;<Icon>arrow_forward</Icon>
              </MDButton>
            </MDBox>
            <SimpleList
              path="/re/units"
              params={{ project_id: id }}
              empty="No units."
              onRowClick={(r) => navigate(`/real-estate/units/${r.id}`)}
              columns={[
                { key: 'unit_number', label: 'Unit' },
                { key: 'building_name', label: 'Building' },
                { key: 'floor', label: 'Floor', align: 'right' },
                { key: 'bedrooms', label: 'Beds', render: (r) => bedroomsLabel(r.bedrooms) },
                { key: 'bua_sqft', label: 'BUA sq ft', align: 'right', render: (r) => formatNumber(r.bua_sqft) },
                { key: 'list_price', label: 'List price', align: 'right', render: (r) => <Num>{formatMoney(r.list_price, r.currency, { decimals: 0 })}</Num> },
                { key: 'status', label: 'Status', render: (r) => <ReStatusChip vocab="unit" status={r.status} size="xs" /> },
              ]}
            />
          </>
        )}
        {tab === 'price_lists' && <PriceLists projectId={id} notify={notify} canEdit={canEdit} />}
        {tab === 'plans' && (
          <SimpleList
            path="/re/payment-plans"
            params={{ project_id: id }}
            empty="No project payment plans (global plans still apply)."
            onRowClick={(r) => navigate(`/real-estate/payment-plans/${r.id}`)}
            columns={[
              { key: 'name', label: 'Plan' },
              { key: 'description', label: 'Description' },
              { key: 'is_post_handover', label: 'Post-handover', render: (r) => (r.is_post_handover ? 'Yes' : 'No') },
              { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
            ]}
          />
        )}
        {tab === 'construction' && (
          <ConstructionUpdates projectId={id} notify={notify} canEdit={canEdit} onProjectChanged={reload} />
        )}
        {tab === 'amenities' && <Amenities projectId={id} project={p} notify={notify} canEdit={canEdit} onSaved={reload} />}
        {tab === 'media' && <Media project={p} />}
      </RePanel>

      <HandoverDialog
        open={handoverOpen}
        onClose={() => setHandoverOpen(false)}
        projectId={id}
        notify={notify}
        onDone={() => { setHandoverOpen(false); reload(); }}
      />
      {snack}
    </RePage>
  );
}
